import {
  Component,
  computed,
  OnDestroy,
  signal,
  inject,
  input,
  ViewChild,
  ElementRef,
  output
} from '@angular/core';
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {ActionCarouselButton} from "./types/carousel.types";
import {actionCarouselButtons} from "./configs/carousel-config";
import {CarouselSwipeService} from './services/carousel-swipe.service';
import {CarouselBreakpointService} from './services/carousel-breakpoint.service';

@Component({
  selector: 'app-carousel',
  imports: [
    MatIcon,
    MatIconButton
  ],
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss'
})
export class CarouselComponent implements OnDestroy {
  itemsCount = input.required<number>();
  visibleItems = input<number>(3);
  slideStep = input<number>(1);
  gap = input<number>(16);
  currentIndexChange = output<number>();
  @ViewChild('viewport', { read: ElementRef }) viewport!: ElementRef<HTMLElement>;

  private carouselSwipeService = inject(CarouselSwipeService);
  private breakpointService = inject(CarouselBreakpointService);
  public readonly actionCarouselButtons = actionCarouselButtons;

  private currentIndexSignal = signal<number>(0);
  public effectiveVisibleItems = computed(() => this.breakpointService.breakpointVisibleItems() ?? this.visibleItems());
  public currentIndex = this.currentIndexSignal;
  public disabled = computed(() => this.itemsCount() === 0 || !this.canSlide());
  public maxIndex = computed(() => Math.max(0, this.itemsCount() - this.effectiveVisibleItems()));
  public canSlide = computed(() => this.itemsCount() > this.effectiveVisibleItems());
  public prevButtonDisabled = computed(() => this.currentIndex() === 0 || !this.canSlide());
  public nextButtonDisabled = computed(() => !this.canSlide() || this.currentIndex() >= this.maxIndex());
  public transformValue = computed(() => `translateX(calc(-1 * ${this.currentIndex()} * ((100% + ${this.gap()}px) / ${this.effectiveVisibleItems()})))`);

  public get dragging() {
    return this.carouselSwipeService.dragging;
  }

  public ngOnDestroy(): void {
    this.resetIndex();
  }

  public handlePointerDown(event: PointerEvent): void {
    if (this.disabled()) { return; }

    this.carouselSwipeService.init(event);
    event.preventDefault();
  }

  public handlePointerMove(event: PointerEvent): void {
    if (!this.carouselSwipeService.dragging() || this.disabled()) { return; }

    const containerWidth = this.viewport?.nativeElement?.offsetWidth || 0;
    const dragShift = this.carouselSwipeService.updateDrag({
      clientX: event.clientX,
      clientY: event.clientY,
      visibleItems: this.effectiveVisibleItems(),
      slideStep: this.slideStep(),
      containerWidth
    });

    dragShift !== null && event.preventDefault();
  }

  public handlePointerUp(event: PointerEvent): void {
    if (this.disabled()) { return; }

    const action = this.carouselSwipeService.handleEnd(event.clientX);
    action && this.handleClick(action);
  }

  public handlePointerCancel(): void {
    this.carouselSwipeService.cancel();
  }

  public handleWheel(event: WheelEvent): void {
    if (this.disabled()) { return; }

    const action = this.carouselSwipeService.handleWheel(event);
    action && this.handleClick(action);
  }

  public handleClick(actionCarouselButton: ActionCarouselButton): void {
    const newIndex = actionCarouselButton === 'prev' ?
      Math.max(0, this.currentIndex() - this.slideStep()) :
      Math.min(this.maxIndex(), this.currentIndex() + this.slideStep());
    this.currentIndexSignal.set(newIndex);
    this.currentIndexChange.emit(newIndex);
  }

  public resetIndex(): void {
    this.currentIndexSignal.set(0);
    this.currentIndexChange.emit(0);
  }
}
